// Global Instructions Rule Applied!
//
// Consolidated Call Position: per-ACCOUNT drawdown position across a fund
// group's member funds (consolidated commitment / called / % drawn /
// uncalled / paid / outstanding), with an expandable per-fund breakdown and per-member
// summary tiles. Mirrors CallPositionView's shape; read-only (calls are
// still issued per fund - one gross call at the primary fund under the
// two-step model - so there is no New-call action here in phase 1).

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Card, Col, Progress, Row, Space, Spin, Table, Typography } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import type { RootState } from '../../../state_manager/store.ts'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { useNumberDisplay } from '../../../core/utils/inputNumberFormat.ts'
import {
  FundGroupPosition,
  FundGroupPositionMember,
  FundGroupPositionRow
} from '../../../state_manager/data_models/fund_group.ts'
import { fetchFundGroupCallPosition } from '../services/fundGroupsService.ts'
import HelpTip from '../../../core/components/HelpTip.tsx'
import { outsideCommitmentHelp } from '../../capital_calls/utils/commitmentTerms.ts'
import ExportRowsButton from '../../shared/table_grid_export/ExportRowsButton.tsx'

const { Text } = Typography

interface Props {
  fundGroupId: string | null
}

export function GroupCallPositionView({ fundGroupId }: Props): React.ReactElement | null {
  const currencyText = useNumberDisplay('currency')
  const groupName = useSelector(
    (s: RootState) => (s.globalConfigs?.fund_groups ?? []).find((g) => g.id === fundGroupId)?.name ?? 'Consolidated fund'
  )
  const [position, setPosition] = useState<FundGroupPosition | null>(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    if (!fundGroupId) return
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundGroupCallPosition(fundGroupId, token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to load consolidated call position')
        return
      }
      setPosition(res.data)
    } finally {
      setLoading(false)
    }
  }, [fundGroupId])

  useEffect(() => {
    void load()
  }, [load])

  const summary = position?.summary ?? null
  const rows = position?.rows ?? []
  const members = position?.members ?? []
  // fees / expenses the PRIMARY fund's terms keep outside the commitment do
  // not draw it down (spec A3.9 addendum part 4): the % drawn and Uncalled
  // headers say which line types are left out; null = everything counts
  const outsideHelp = outsideCommitmentHelp(
    summary?.commitment_includes_mgmt_fees,
    summary?.commitment_includes_expenses
  )

  const columns = useMemo(
    () => [
      {
        title: 'Account',
        dataIndex: 'accounts__name',
        key: 'account',
        fixed: 'left' as const,
        render: (v: string | null) => v ?? '—'
      },
      {
        title: 'Consolidated Commitment',
        dataIndex: 'prog__subscription_amount',
        key: 'subscription',
        align: 'right' as const,
        render: (v: number | null) => currencyText(v ?? 0)
      },
      {
        title: 'Called to date',
        dataIndex: 'prog__called_to_date',
        key: 'called',
        align: 'right' as const,
        render: (v: number | null) => <Text strong>{currencyText(v ?? 0)}</Text>
      },
      {
        title: (
          <span>
            % drawn
            <HelpTip title={outsideHelp} />
          </span>
        ),
        dataIndex: 'prog__pct_drawn',
        key: 'pct_drawn',
        width: 140,
        render: (v: number | null) => (
          <Progress
            percent={Math.round((v ?? 0) * 1000) / 10}
            size='small'
            format={(p) => `${(p ?? 0).toFixed(1)}%`}
          />
        )
      },
      {
        title: (
          <span>
            Uncalled
            <HelpTip title={outsideHelp} />
          </span>
        ),
        dataIndex: 'prog__uncalled',
        key: 'uncalled',
        align: 'right' as const,
        render: (v: number | null) => currencyText(v ?? 0)
      },
      {
        title: 'Paid',
        dataIndex: 'prog__paid_amount',
        key: 'paid',
        align: 'right' as const,
        render: (v: number | null) => currencyText(v ?? 0)
      },
      {
        title: 'Outstanding',
        dataIndex: 'prog__outstanding',
        key: 'outstanding',
        align: 'right' as const,
        render: (v: number | null) => {
          const value = v ?? 0
          if (Math.abs(value) < 0.005) return <Text type='secondary'>—</Text>
          return <Text type='danger'>{currencyText(value)}</Text>
        }
      }
    ],
    [currencyText, outsideHelp]
  )

  const pocketColumns = useMemo(
    () => [
      {
        title: 'Fund',
        key: 'fund',
        render: (_: unknown, m: FundGroupPositionMember) => (
          <span>
            {m.funds__name ?? '—'}
            {m.fund_group_members__is_primary ? <Text type='secondary'> (primary)</Text> : null}
          </span>
        )
      },
      {
        title: 'Fund Commitment',
        key: 'amount',
        align: 'right' as const,
        // per-account pocket rows carry fund_commitments__amount; the
        // member-fund summary rows carry the aggregated prog__committed
        render: (_: unknown, m: FundGroupPositionMember) =>
          currencyText(m.fund_commitments__amount ?? m.prog__committed ?? 0)
      },
      {
        title: 'Called to date',
        key: 'called',
        align: 'right' as const,
        render: (_: unknown, m: FundGroupPositionMember) => currencyText(m.prog__called_to_date ?? 0)
      },
      {
        title: 'Uncalled',
        key: 'uncalled',
        align: 'right' as const,
        render: (_: unknown, m: FundGroupPositionMember) => currencyText(m.prog__uncalled ?? 0)
      },
      {
        title: 'Paid',
        key: 'paid',
        align: 'right' as const,
        render: (_: unknown, m: FundGroupPositionMember) => currencyText(m.prog__paid_amount ?? 0)
      },
      {
        title: 'Outstanding',
        key: 'outstanding',
        align: 'right' as const,
        render: (_: unknown, m: FundGroupPositionMember) => currencyText(m.prog__outstanding ?? 0)
      }
    ],
    [currencyText]
  )

  const tiles = useMemo(() => {
    if (!summary) return []
    return [
      { label: 'Committed (consolidated)', value: currencyText(summary.committed ?? 0) },
      {
        label: 'Called to date',
        value: currencyText(summary.called ?? 0),
        sub: `${(((summary.pct_drawn ?? 0) * 1000) / 10).toFixed(1)}% drawn`
      },
      { label: 'Uncalled', value: currencyText(summary.uncalled ?? 0), help: outsideHelp },
      {
        label: 'Outstanding',
        value: currencyText(summary.outstanding ?? 0),
        danger: Math.abs(summary.outstanding ?? 0) >= 0.005
      }
    ]
  }, [summary, currencyText, outsideHelp])

  if (!fundGroupId) return null

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex items-center justify-end'>
        <Space>
          <ExportRowsButton
            name={`${groupName} Account position`}
            rows={rows}
            columns={[
              { title: 'Account', value: (r) => r.accounts__name },
              { title: 'Consolidated Commitment', value: (r) => r.prog__subscription_amount },
              { title: 'Called to date', value: (r) => r.prog__called_to_date },
              { title: '% drawn', value: (r) => r.prog__pct_drawn, kind: 'percent' },
              { title: 'Uncalled', value: (r) => r.prog__uncalled },
              { title: 'Paid', value: (r) => r.prog__paid_amount },
              { title: 'Outstanding', value: (r) => r.prog__outstanding }
            ]}
          />
          <Button icon={<ReloadOutlined />} size='small' onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
        </Space>
      </div>

      {summary ? (
        <Row gutter={[12, 12]}>
          {tiles.map((tile) => (
            <Col xs={12} md={6} key={tile.label}>
              <Card size='small'>
                <div className='text-xs text-gray-500'>
                  {tile.label}
                  <HelpTip title={tile.help} />
                </div>
                <div className='text-lg font-medium'>
                  {tile.danger ? <Text type='danger'>{tile.value}</Text> : tile.value}
                </div>
                {tile.sub ? <div className='text-xs text-gray-500'>{tile.sub}</div> : null}
              </Card>
            </Col>
          ))}
        </Row>
      ) : null}

      {members.length > 0 ? (
        <Card size='small' title='Per member fund'>
          <Table
            size='small'
            pagination={false}
            rowKey={(m: FundGroupPositionMember) => String(m.funds__id ?? '')}
            dataSource={members}
            columns={pocketColumns}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      ) : null}

      <Card size='small' title='Account position (consolidated)'>
        {loading ? (
          <div className='flex justify-center p-6'>
            <Spin />
          </div>
        ) : (
          <Table
            size='small'
            pagination={false}
            rowKey={(r: FundGroupPositionRow) => String(r.accounts__id ?? r.accounts__name ?? '')}
            dataSource={rows}
            columns={columns}
            scroll={{ x: 'max-content' }}
            expandable={{
              rowExpandable: (r: FundGroupPositionRow) => (r.prog__members?.length ?? 0) > 0,
              expandedRowRender: (r: FundGroupPositionRow) => (
                <Table
                  size='small'
                  pagination={false}
                  rowKey={(m: FundGroupPositionMember) => String(m.fund_commitments__id ?? m.funds__id ?? '')}
                  dataSource={r.prog__members ?? []}
                  columns={pocketColumns}
                />
              )
            }}
            summary={() => {
              if (!summary) return null
              const cells: React.ReactNode[] = [
                <Text strong key='label'>Total</Text>,
                <Text strong key='committed'>{currencyText(summary.committed ?? 0)}</Text>,
                <Text strong key='called'>{currencyText(summary.called ?? 0)}</Text>,
                <Text strong key='pct'>{`${(((summary.pct_drawn ?? 0) * 1000) / 10).toFixed(1)}%`}</Text>,
                <Text strong key='uncalled'>{currencyText(summary.uncalled ?? 0)}</Text>,
                <Text strong key='paid'>{currencyText(summary.paid ?? 0)}</Text>,
                <Text strong key='outstanding'>{currencyText(summary.outstanding ?? 0)}</Text>
              ]
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    {/* the expand column occupies index 0 */}
                    <Table.Summary.Cell index={0} key='expand' />
                    {cells.map((c, idx) => (
                      <Table.Summary.Cell
                        index={idx + 1}
                        key={idx}
                        align={idx === 0 || idx === 3 ? undefined : 'right'}
                      >
                        {c}
                      </Table.Summary.Cell>
                    ))}
                  </Table.Summary.Row>
                </Table.Summary>
              )
            }}
          />
        )}
      </Card>
    </div>
  )
}

export default GroupCallPositionView
